/**
 * Client-side PDF export for maintenance checklists.
 * jsPDF is imported dynamically so it never enters the SSR/initial bundle.
 */

export interface PdfSection {
  heading: string;
  items: { label: string; note?: string }[];
}

export interface MaintenancePdfInput {
  title: string;
  subtitle?: string;
  intro?: string;
  sections: PdfSection[];
  filename: string;
}

const TEAL: [number, number, number] = [14, 124, 134];
const INK: [number, number, number] = [35, 43, 58];
const GREY: [number, number, number] = [110, 118, 132];
const LINE: [number, number, number] = [214, 219, 226];

export async function downloadMaintenancePdf(input: MaintenancePdfInput) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 48;
  const contentW = pageW - M * 2;
  let y = M;

  const footer = () => {
    const page = doc.getNumberOfPages();
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...GREY);
    doc.text("AutoSpecData — vehicle specs & maintenance data", M, pageH - 26);
    doc.text(`Page ${page}`, pageW - M, pageH - 26, { align: "right" });
  };

  const newPage = () => {
    footer();
    doc.addPage();
    y = M;
  };

  const need = (h: number) => {
    if (y + h > pageH - 56) newPage();
  };

  // Header band
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, pageW, 6, "F");

  doc.setFont("helvetica", "bold").setFontSize(20).setTextColor(...INK);
  const titleLines = doc.splitTextToSize(input.title, contentW);
  doc.text(titleLines, M, (y += 28));
  y += (titleLines.length - 1) * 24;

  if (input.subtitle) {
    doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(...TEAL);
    doc.text(input.subtitle, M, (y += 18));
  }

  if (input.intro) {
    doc.setFont("helvetica", "normal").setFontSize(9.5).setTextColor(...GREY);
    const lines = doc.splitTextToSize(input.intro, contentW);
    doc.text(lines, M, (y += 20));
    y += (lines.length - 1) * 12;
  }

  y += 16;
  doc.setDrawColor(...LINE).line(M, y, pageW - M, y);
  y += 8;

  for (const section of input.sections) {
    need(60);
    doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(...TEAL);
    doc.text(section.heading, M, (y += 24));
    y += 6;

    for (const item of section.items) {
      const labelLines = doc.splitTextToSize(item.label, contentW - 26);
      const noteLines = item.note
        ? doc.splitTextToSize(item.note, contentW - 26)
        : [];
      need(labelLines.length * 13 + noteLines.length * 11 + 12);

      y += 14;
      // checkbox
      doc.setDrawColor(...TEAL).setLineWidth(0.8);
      doc.rect(M, y - 8.5, 10, 10);

      doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(...INK);
      doc.text(labelLines, M + 20, y);
      y += (labelLines.length - 1) * 12;

      if (noteLines.length) {
        doc.setFontSize(8.5).setTextColor(...GREY);
        doc.text(noteLines, M + 20, (y += 11));
        y += (noteLines.length - 1) * 10;
      }
    }
    y += 10;
  }

  need(50);
  y += 16;
  doc.setDrawColor(...LINE).setLineWidth(0.5).line(M, y, pageW - M, y);
  doc.setFont("helvetica", "italic").setFontSize(8).setTextColor(...GREY);
  const disc = doc.splitTextToSize(
    "Intervals reflect mainstream manufacturer practice for this powertrain and era. Your owner's manual is the authority — follow its severe-duty schedule if you tow, idle often, or drive mostly short trips.",
    contentW,
  );
  doc.text(disc, M, (y += 14));

  footer();
  doc.save(input.filename);
}

export function slugifyFilename(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
